import { useState } from "react";
import solutionsImage from "@assets/images/Frame-1618869433@2x.png";
import { Reveal } from "@components/ui";

/* ─── Icons ─── */

function HouseIcon({ active }: { active: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 3L4 9V21H9V14H15V21H20V9L12 3Z"
        stroke={active ? "#f24d2b" : "#62636c"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={active ? "#f24d2b20" : "none"}
      />
    </svg>
  );
}

function BuildingIcon({ active }: { active: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M3 21H21M5 21V5C5 4.44772 5.44772 4 6 4H14C14.5523 4 15 4.44772 15 5V21M9 8H11M9 12H11M9 16H11M19 21V11C19 10.4477 18.5523 10 18 10H15"
        stroke={active ? "#f24d2b" : "#62636c"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={active ? "#f24d2b20" : "none"}
      />
    </svg>
  );
}

function WrenchIcon({ active }: { active: boolean }) {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94L6.73 20.2a2 2 0 0 1-2.83 0l-.1-.1a2 2 0 0 1 0-2.83l6.73-6.73A6 6 0 0 1 18.47 2.53L14.7 6.3Z"
        stroke={active ? "#f24d2b" : "#62636c"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={active ? "#f24d2b20" : "none"}
      />
    </svg>
  );
}

function ChevronIcon({ active }: { active: boolean }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={`transition-transform duration-300 ${active ? "rotate-180" : ""}`}
    >
      <path
        d="M5 7.5L10 12.5L15 7.5"
        stroke={active ? "#f24d2b" : "#62636c"}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const iconMap = [HouseIcon, BuildingIcon, WrenchIcon] as const;

/* ─── Sub-components ─── */

interface AccordionItemProps {
  title: string;
  description?: string;
  isActive: boolean;
  onClick: () => void;
  icon: (props: { active: boolean }) => React.ReactNode;
}

function AccordionItem({
  title,
  description,
  isActive,
  onClick,
  icon: Icon,
}: AccordionItemProps) {
  return (
    <div
      className={`w-full rounded-[14px] cursor-pointer transition-all duration-300 border overflow-hidden ${
        isActive
          ? "bg-white border-[#e8e9ed] shadow-[0_4px_20px_rgba(0,0,0,0.06)]"
          : "bg-transparent border-transparent hover:bg-[#f8f9fb]"
      }`}
      onClick={onClick}
    >
      {/* Card inner with left accent */}
      <div
        className={`flex flex-col gap-2 p-4 md:p-5 border-l-[3px] transition-colors duration-300 ${
          isActive ? "border-l-[#f24d2b]" : "border-l-transparent"
        }`}
      >
        {/* Title row */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={`flex items-center justify-center w-8 h-8 md:w-9 md:h-9 rounded-[10px] transition-colors duration-300 ${
                isActive ? "bg-[#f24d2b]/10" : "bg-[#122d63]/[0.06]"
              }`}
            >
              <Icon active={isActive} />
            </div>
            <h2 className="text-[15px] md:text-[18px] lg:text-[22px] tracking-[-0.04em] font-semibold text-[#122d63] font-albert-sans">
              {title}
            </h2>
          </div>
          <ChevronIcon active={isActive} />
        </div>

        {/* Smooth accordion content */}
        <div
          className="transition-[grid-template-rows] duration-500 ease-expo-out grid"
          style={{ gridTemplateRows: isActive ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            {description && (
              <p className="pt-1 pl-11 md:pl-12 text-[13px] md:text-[15px] lg:text-[17px] tracking-[-0.04em] text-[#62636c] font-albert-sans leading-[1.5]">
                {description}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const solutions = [
  {
    title: "Residential Gas Supply",
    description:
      "Gaz Investment in Oil and Gas Supply L.L.C. delivers modern, customer focused gas services across the UAE from household supply to industrial installations.",
  },
  {
    title: "Commercial & Industrial",
    description:
      "Comprehensive gas solutions for businesses and industrial facilities with scalable supply options.",
  },
  {
    title: "Maintenance & Safety",
    description:
      "Regular maintenance services and safety inspections to ensure optimal performance and compliance.",
  },
];

/* ─── Main component ─── */

export function GasSolutionsSection() {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section className="w-full bg-white py-10 md:py-14 lg:py-24 px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto flex flex-col lg:flex-row items-center flex-wrap content-center gap-y-16 gap-x-20">
        {/* -- Image -- */}
        <Reveal
          direction="left"
          className="relative w-full max-w-[500px] lg:w-[664px] h-auto lg:h-[716px] shrink-0 overflow-hidden rounded-[20px]"
        >
          <img
            src={solutionsImage}
            alt="Gas solutions"
            className="w-full h-full object-contain"
            loading="lazy"
          />
        </Reveal>

        {/* -- Content -- */}
        <Reveal direction="right" delay={150} className="flex-1 min-w-[335px]">
          <section className="flex flex-col items-start gap-6 lg:gap-8">
            <h2 className="text-[22px] md:text-[32px] lg:text-[44px] tracking-[-0.04em] font-bold text-[#122d63] font-albert-sans leading-[1.15]">
              Comprehensive Gas Solutions Designed Around You
            </h2>

            {/* Accordion */}
            <div className="w-full flex flex-col gap-2.5">
              {solutions.map((solution, index) => (
                <AccordionItem
                  key={index}
                  title={solution.title}
                  description={solution.description}
                  isActive={activeIndex === index}
                  onClick={() => setActiveIndex(index)}
                  icon={iconMap[index]}
                />
              ))}
            </div>

            {/* CTA Button */}
            <button className="group flex items-center gap-2.5 py-2.5 px-5 rounded-full bg-[#f24d2b]/10 text-[#f24d2b] hover:bg-[#f24d2b] hover:text-white transition-all duration-300 font-albert-sans">
              <span className="text-[14px] md:text-[16px] lg:text-[18px] tracking-[-0.04em] font-semibold">
                Submit Meter Reading
              </span>
              <svg
                viewBox="0 0 24 24"
                fill="none"
                className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5"
              >
                <path
                  d="M5 12H19M19 12L13 6M19 12L13 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </section>
        </Reveal>
      </div>
    </section>
  );
}
